import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Stack,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ResetIcon from "@mui/icons-material/RestartAlt";
import { useLottery } from "../../hooks/useLottery";
import { LotterySessionDto } from "../../models/Lottery";
import ChristmasLoader from "../../Components/ChristmasLoader";

const LotteryForm = () => {
  const { id } = useParams<{ id: string }>();
  const { lottery, updateLottery, resetLottery, addLottery, loading } =
    useLottery(id);
  const navigate = useNavigate();

  const isEditing = !!id;

  const [formData, setFormData] = useState<LotterySessionDto>({
    title: "",
    description: "",
    status: "Pending",
    totalTickets: 0,
    createdAt: new Date(),
    drawDateUtc: null,
    prizes: [],
    sessionId: null,
    totalPrizes: 0,
    drawnPrizesCount: 0,
  });

  useEffect(() => {
    if (!lottery) return;

    setFormData(lottery);
  }, [lottery]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEditing && id) {
        await updateLottery(formData);
      } else {
        await addLottery(formData);
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return <ChristmasLoader size={240} sparkle={true} />;
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "hsl(30, 50%, 98%)", py: 4 }}>
      <Container maxWidth="md">
        <Button
          onClick={() => navigate("/lotteries")}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3, color: "hsl(150, 60%, 25%)" }}
        >
          Πισω στις λοταριες
        </Button>

        <Paper
          sx={{
            p: 4,
            borderRadius: 4,
            border: "2px solid hsl(30, 20%, 85%)",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Mountains of Christmas", cursive',
              color: "hsl(150, 60%, 25%)",
              mb: 4,
              fontSize: { xs: "1.8rem", md: "2.2rem" },
            }}
          >
            {isEditing
              ? "✏️ Επεξεργασία Λοταρίας"
              : "🎄 Δημιουργία Νέας Λοταρίας"}
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <TextField
              label="Τίτλος Λοταρίας"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              required
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(150, 60%, 25%)",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "hsl(150, 60%, 25%)",
                },
              }}
            />

            {/* Description */}
            <TextField
              label="Περιγραφή"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              multiline
              rows={3}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(150, 60%, 25%)",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "hsl(150, 60%, 25%)",
                },
              }}
            />
            {/* Total tickets */}
            <TextField
              label="Σύνολο Λαχνών"
              type="number"
              value={formData.totalTickets}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  totalTickets: Number(e.target.value),
                }))
              }
              required
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(150, 60%, 25%)",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "hsl(150, 60%, 25%)",
                },
              }}
            />
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  "&.Mui-focused": { color: "hsl(150, 60%, 25%)" },
                }}
              >
                Κατάσταση
              </InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: +e.target.value as any,
                  }))
                }
                sx={{
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "hsl(150, 60%, 25%)",
                  },
                }}
              >
                <MenuItem value={0}>Επερχόμενη</MenuItem>
                <MenuItem value={1}>Ενεργή</MenuItem>
                <MenuItem value={2}>Ολοκληρωμένη</MenuItem>
                <MenuItem value={3}>Ακυρωμένη</MenuItem>
              </Select>
            </FormControl>
            <Stack spacing={2} direction="row" justifyContent="flex-end">
              {formData.sessionId && (
                <Button
                  variant="contained"
                  onClick={() => {
                    const confirmed = window.confirm(
                      "Είσαι σίγουρος ότι θέλεις να αρχικοποιήσης αυτή τη λοταρία;"
                    );
                    if (!confirmed) return;
                    resetLottery(lottery?.sessionId as string);
                  }}
                  size="large"
                  startIcon={<ResetIcon />}
                  disabled={loading}
                  sx={{
                    mt: 2,
                    background:
                      "linear-gradient(135deg, hsla(202, 86%, 22%, 1.00) 0%, hsla(207, 99%, 31%, 1.00) 100%)",
                    color: "white",
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 2,
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, hsla(239, 88%, 55%, 1.00) 0%, hsla(194, 56%, 27%, 1.00) 100%)",
                    },
                  }}
                >
                  Επαναφορά Κλήρωσης
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                disabled={loading}
                sx={{
                  mt: 2,
                  background:
                    "linear-gradient(135deg, hsl(350, 80%, 40%) 0%, hsl(350, 85%, 30%) 100%)",
                  color: "white",
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 2,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, hsl(350, 75%, 45%) 0%, hsl(350, 80%, 35%) 100%)",
                  },
                }}
              >
                {isEditing ? "Αποθηκευση" : "καταχωρηση"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LotteryForm;
